import type { ClaimTemplate } from 'core/claim';
import { useLocalSearchParams } from 'expo-router';
import { useRevalidation } from 'hooks/useRevalidation';
import { ClaimApi } from 'integration/aggregate';
import type { ClaimStackScreenProps } from 'navigation';
import type React from 'react';
import { useCallback } from 'react';
import { NotFoundScreen } from 'screens/NotFoundScreen';
import { trpc } from 'trpc';

import { ScreenComponent } from './internal';

const api = new ClaimApi();

type RouterParams = {
  id: string;
};

export const ClaimScreen: React.FC<ClaimStackScreenProps<'View'>> = () => {
  const { id } = useLocalSearchParams<RouterParams>();

  const {
    data,
    isFetching: isValidating,
    isError: error,
  } = trpc.claim.getById.useQuery({ id });

  const revalidate = useRevalidation(id);

  const onSubmit = useCallback(async () => {
    await api.submit(id);
    await revalidate();
  }, [id, revalidate]);

  const onEdit = useCallback(
    async (result: ClaimTemplate) => {
      await api.update({
        id,
        chargeLines: result.claimLines.map((line) => ({
          chargeLine: line.chargeItemIdentifier,
          quantity: line.quantity,
        })),
      });
      await revalidate();
    },
    [id, revalidate],
  );

  if (error) {
    return <NotFoundScreen />;
  }

  return (
    <ScreenComponent
      responseData={{ data, isValidating, error }}
      onSubmit={onSubmit}
      onEdit={onEdit}
    />
  );
};
